import cs from 'classnames';
import type { CommunityDetail, LabelInValue } from 'models';
import { useState } from 'react';
import {
  Button,
  Col,
  Form,
  FormActions,
  GroupLineIcon,
  Item,
  LinkLine,
  MailLineIcon,
  Message2LineIcon,
  Modal,
  QrCodeLineIcon,
  Row,
  Text,
  message,
} from 'ui';
import { copyToClipboard } from 'features/copy-to-clipboard';
import { CommunitySelect, useCommunitySelectCreateOption } from '../../components/community-select';
import { useGetInvitationTokenMutation } from '../../queries/mutations/useGetInvitationTokenMutation';
import { CommunityInfo } from './community-info';
import { EmailInviteModal } from './email-invite-modal';
import { useGenerateInvitationLink } from './hooks';
import { MembersInviteModal } from './members-invite-modal';
import { ShareQrCodeModal } from './share-qr-code-modal';
import { SmsInviteModal } from './sms-invite-modal';

type InviteFriendsFormValues = {
  community: LabelInValue;
};

type ModalState =
  | {
      community: LabelInValue;
      type: 'email' | 'members' | 'qr' | 'sms';
    }
  | {
      community: undefined;
      type: undefined;
    };

type P = {
  community?: CommunityDetail;
  isOpened: boolean;
  onCancel: () => void;
};

type ValidateAndExecuteCallback = (values: InviteFriendsFormValues) => Promise<void> | void;

const InviteFriendsModal = ({ community, isOpened, onCancel }: P) => {
  const [form] = Form.useForm<InviteFriendsFormValues>();
  const [modalsOpen, setModalsOpen] = useState<ModalState>({ community: undefined, type: undefined });
  const createOption = useCommunitySelectCreateOption();

  const { isPending: isGeneratingToken, mutateAsync: getToken } = useGetInvitationTokenMutation();
  const generateInvitationLink = useGenerateInvitationLink();

  const handleTokenClipboard = async ({ communityId }: { communityId: string }) => {
    await copyToClipboard(async () => {
      const token = await getToken({ communityId });
      return generateInvitationLink({ communityId, token });
    });

    message.success('Invitation link successfully copied to clipboard');
  };

  const validateAndExecute = async (callback: ValidateAndExecuteCallback) => {
    try {
      const values = await form.validateFields();
      await callback(values);
    } catch {}
  };

  const buttons = [
    {
      icon: <LinkLine />,
      isLoading: isGeneratingToken,
      key: 'link',
      onClick: async () =>
        validateAndExecute(async (values: InviteFriendsFormValues) =>
          handleTokenClipboard({ communityId: values.community.value }),
        ),
      title: 'Copy link',
    },
    {
      icon: <QrCodeLineIcon />,
      key: 'qr-code',
      onClick() {
        void validateAndExecute((values: InviteFriendsFormValues) => {
          setModalsOpen({ community: values.community, type: 'qr' });
        });
      },
      title: 'QR code',
    },
    {
      icon: <GroupLineIcon />,
      key: 'members',
      onClick() {
        void validateAndExecute((values: InviteFriendsFormValues) => {
          setModalsOpen({ community: values.community, type: 'members' });
        });
      },
      title: 'Member select',
    },
    {
      icon: <MailLineIcon />,
      key: 'email',
      onClick() {
        void validateAndExecute((values: InviteFriendsFormValues) => {
          setModalsOpen({ community: values.community, type: 'email' });
        });
      },
      title: 'E-mail',
    },
    {
      icon: <Message2LineIcon />,
      key: 'sms',
      onClick() {
        void validateAndExecute((values: InviteFriendsFormValues) => {
          setModalsOpen({ community: values.community, type: 'sms' });
        });
      },
      title: 'SMS',
    },
  ];

  const initialValues = community
    ? {
        community: {
          label: community.name,
          value: community.id,
        },
      }
    : undefined;

  return (
    <>
      <Modal
        className={cs({
          'visually-hidden': modalsOpen.type !== undefined,
        })}
        isOpened={isOpened}
        onCancel={onCancel}
        title="Invite friends"
      >
        <Modal.HugContent>
          <Form form={form} initialValues={initialValues}>
            <Item
              extra={
                community
                  ? undefined
                  : 'You can invite friends only to public communities and those where you are an admin'
              }
              hideLabel={Boolean(community)}
              label="Select community"
              name="community"
              rules={[{ required: true }]}
            >
              {community ? (
                <CommunityInfo community={community} />
              ) : (
                <CommunitySelect
                  createOption={createOption}
                  query={{ filter: { privacy: 'public', scope: 'joined' } }}
                />
              )}
            </Item>
          </Form>
          <Row gutter={[0, 'xs']}>
            <Col span={24}>
              <Text size="small">How would you like to invite someone?</Text>
            </Col>
            {buttons.map(({ key, ...button }) => (
              <Col key={key} span={24}>
                <Button block size="large" type="secondary" {...button}>
                  {button.title}
                </Button>
              </Col>
            ))}
          </Row>
          <Modal.HugContent.Footer>
            <FormActions right={{ children: 'Cancel', onClick: onCancel }} />
          </Modal.HugContent.Footer>
        </Modal.HugContent>
      </Modal>
      {/* TODO: in these components should be community as required prop */}
      <ShareQrCodeModal
        community={modalsOpen.community}
        isOpened={modalsOpen.type === 'qr'}
        onCancel={() => {
          setModalsOpen({ community: undefined, type: undefined });
        }}
      />
      <MembersInviteModal
        community={modalsOpen.community}
        isOpened={modalsOpen.type === 'members'}
        onCancel={() => {
          setModalsOpen({ community: undefined, type: undefined });
        }}
      />
      <EmailInviteModal
        community={modalsOpen.community}
        isOpened={modalsOpen.type === 'email'}
        onCancel={() => {
          setModalsOpen({ community: undefined, type: undefined });
        }}
      />
      <SmsInviteModal
        community={modalsOpen.community}
        isOpened={modalsOpen.type === 'sms'}
        onCancel={() => {
          setModalsOpen({ community: undefined, type: undefined });
        }}
      />
    </>
  );
};

export default InviteFriendsModal;
